import React, { useState } from 'react';
import { 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  Button, 
  TextField, 
  Stack, 
  Box
} from '@mui/material';
import api from '../api/api';
import { useFeedback } from '../context/FeedbackContext';

interface Props {
  open: boolean;
  onClose: () => void;
  schoolId: number;
  onSuccess: () => void;
}

const AddCameraDialog: React.FC<Props> = ({ open, onClose, schoolId, onSuccess }) => {
  const [name, setName] = useState('');
  const [source, setSource] = useState('');
  const [location, setLocation] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { showSuccess, showError } = useFeedback();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    
    if (!name.trim()) {
      setError('Name is required');
      return;
    }
    
    if (!source.trim()) {
      setError('Source is required');
      return;
    }

    setLoading(true);
    try {
      await api.post('/cameras', {
        name: name.trim(),
        source: source.trim(),
        location: location.trim(),
        school_id: schoolId,
      });
      showSuccess('Camera added successfully!');
      onSuccess();
      handleClose();
    } catch (err: any) {
      showError(err?.response?.data?.detail || 'Failed to add camera');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setName('');
    setSource('');
    setLocation('');
    setError('');
    setLoading(false);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Add New Camera</DialogTitle>
      <Box component="form" onSubmit={handleSubmit}>
        <DialogContent>
          <Stack spacing={3}>
            <TextField
              label="Name"
              value={name}
              onChange={e => setName(e.target.value)}
              required
              fullWidth
              error={!!error && !name.trim()}
              helperText={!name.trim() && error ? 'Name is required' : ''}
            />
            
            <TextField
              label="Source"
              value={source}
              onChange={e => setSource(e.target.value)}
              required
              fullWidth
              placeholder="e.g., 0, 1, 2, rtsp://..."
              error={!!error && !source.trim()}
              helperText={!source.trim() && error ? 'Source is required' : ''}
            />
            
            <TextField
              label="Location"
              value={location}
              onChange={e => setLocation(e.target.value)}
              fullWidth
              placeholder="e.g., Main Entrance, Classroom 101"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={loading}>
            Cancel
          </Button>
          <Button 
            type="submit" 
            variant="contained" 
            disabled={loading || !name.trim() || !source.trim()}
          >
            {loading ? 'Adding...' : 'Add Camera'}
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
};

export default AddCameraDialog; 